import { z } from 'zod';
import { DataLayerDraftStore, DATA_LAYER_DRAFT_TTL_MS } from './data-layer-drafts.js';
import type { DataLayer } from '../shared/data-layer.js';
import { compressText, decodeCompressedJson, replaceUrlState } from './url-state.js';
import { parseCompactSharedMapState } from './shared-map-state.js';

const DRAFT_PREFIX = 'drawonamap.checkoutMapDraft.';
const CHECKOUT_RETURN_TO_KEY = 'drawonamap.proCheckoutReturnTo';
const draftSchema = z.object({
  path: z.string(),
  expiresAt: z.number(),
  encoded: z.string(),
  dataLayerIds: z.array(z.string()),
  isCreator: z.boolean(),
  title: z.string().nullish(),
});

export function rememberCheckoutReturnTo(returnTo: string): void {
  window.sessionStorage.setItem(CHECKOUT_RETURN_TO_KEY, returnTo);
  window.localStorage.setItem(CHECKOUT_RETURN_TO_KEY, returnTo);
}

async function removeDataCopies(ids: string[]): Promise<void> {
  const store = new DataLayerDraftStore();
  await Promise.all(ids.map((id) => store.delete(id)));
}

async function removeExpiredDrafts(): Promise<void> {
  const expiredIds: string[] = [];
  for (const key of Object.keys(window.localStorage)) {
    if (!key.startsWith(DRAFT_PREFIX)) continue;
    try {
      const result = draftSchema.safeParse(JSON.parse(window.localStorage.getItem(key) ?? 'null'));
      if (result.success && result.data.expiresAt > Date.now()) continue;
      if (result.success) expiredIds.push(...result.data.dataLayerIds);
    } catch { /* Remove invalid draft records. */ }
    window.localStorage.removeItem(key);
  }
  await removeDataCopies(expiredIds);
}

export async function finishCheckoutMapDraft(dataLayerIds: string[], encodedHash?: string, draftId?: string): Promise<void> {
  const url = new URL(window.location.href);
  const id = draftId ?? url.searchParams.get('checkout_draft');
  try {
    if (id) window.localStorage.removeItem(`${DRAFT_PREFIX}${id}`);
    await removeDataCopies(dataLayerIds);
  } catch (error) { console.warn('Checkout draft cleanup failed:', error); }
  url.searchParams.delete('checkout_draft');
  if (encodedHash) url.hash = encodedHash;
  replaceUrlState(`${url.pathname}${url.search}${url.hash}`);
}

export async function saveCheckoutMapDraft(json: string, dataLayers: DataLayer[], isCreator: boolean, title?: string | null): Promise<string> {
  await removeExpiredDrafts();
  const url = new URL(window.location.href);
  url.searchParams.delete('checkout_draft');
  const shortMap = url.pathname.match(/^\/m\/([^/]+)\/?$/);
  if (shortMap) {
    url.pathname = '/';
    url.searchParams.set('map', decodeURIComponent(shortMap[1]));
  }
  const path = `${url.pathname}${url.search}`;
  const store = new DataLayerDraftStore();
  const dataLayerIds: string[] = [];
  const id = crypto.randomUUID();
  try {
    for (const layer of dataLayers) dataLayerIds.push(await store.put({ ...layer, draftId: undefined }));
    const encoded = await compressText(json);
    window.localStorage.setItem(`${DRAFT_PREFIX}${id}`, JSON.stringify({
      path, encoded, dataLayerIds, isCreator, title: title ?? undefined, expiresAt: Date.now() + DATA_LAYER_DRAFT_TTL_MS,
    }));
    url.searchParams.set('checkout_draft', id);
    const returnTo = `${url.pathname}${url.search}${url.hash}`;
    rememberCheckoutReturnTo(returnTo);
    return returnTo;
  } catch (error) {
    try {
      window.localStorage.removeItem(`${DRAFT_PREFIX}${id}`);
      await removeDataCopies(dataLayerIds);
    } catch (cleanupError) { console.warn('Checkout draft cleanup failed:', cleanupError); }
    throw error;
  }
}

export async function readCheckoutMapDraft() {
  const url = new URL(window.location.href);
  const id = url.searchParams.get('checkout_draft');
  if (!id || !/^[a-f0-9-]{36}$/.test(id)) return null;
  const raw = window.localStorage.getItem(`${DRAFT_PREFIX}${id}`);
  if (!raw) throw new Error('Checkout map draft is unavailable');
  const result = draftSchema.safeParse(JSON.parse(raw));
  if (!result.success || result.data.expiresAt <= Date.now()) {
    await removeExpiredDrafts();
    throw new Error('Checkout map draft is invalid or expired');
  }
  const draft = result.data;
  const target = new URL(draft.path, url.origin);
  if (target.pathname !== url.pathname || target.searchParams.get('map') !== url.searchParams.get('map')) return null;
  const parsed = parseCompactSharedMapState(await decodeCompressedJson(draft.encoded));
  if (!parsed) throw new Error('Checkout map draft is invalid');
  const store = new DataLayerDraftStore();
  const dataLayers: DataLayer[] = [];
  for (const layerId of draft.dataLayerIds) {
    const layer = await store.get(layerId);
    if (!layer) throw new Error('Checkout data layer is unavailable');
    dataLayers.push({ ...layer, draftId: undefined });
  }
  return { ...draft, id, parsed, dataLayers };
}
